import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πώς να επιλέξω tattoo artist στην Αθήνα;", acceptedAnswer: { "@type": "Answer", text: "Κοίτα το portfolio του artist, ειδικά healed δουλειές. Σιγουρέψου ότι το στυλ ταιριάζει με αυτό που θέλεις και ρώτα για consultation πριν κλείσεις ραντεβού." } },
    { "@type": "Question", name: "Τι να κοιτώ στο portfolio ενός tattoo artist;", acceptedAnswer: { "@type": "Answer", text: "Κοίτα κυρίως healed δουλειές, όχι μόνο fresh. Δες αν ο artist έχει εμπειρία στο στυλ που θέλεις, κάθε στυλ απαιτεί διαφορετική τεχνική." } },
    { "@type": "Question", name: "Είναι σημαντική η υγιεινή σε ένα τατουάζ studio;", acceptedAnswer: { "@type": "Answer", text: "Απολύτως. Αποστειρωμένος εξοπλισμός, single-use βελόνες και καθαρό περιβάλλον είναι μη διαπραγματεύσιμα. Ένα σοβαρό studio δεν θα ενοχληθεί αν ρωτήσεις." } },
  ],
};

export default function TattooArtistAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πώς να διαλέξεις tattoo artist στην Αθήνα",
      "description": "Τι να προσέξεις πριν επιλέξεις tattoo studio στην Αθήνα: portfolio, στυλ, υγιεινή, consultation και red flags από το 210 Tattoo Athens.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-24",
      "dateModified": "2026-04-24",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-artist-athens",
      "inLanguage": "el"
    };
    inject('schema-tattoo-artist-faq', faqSchema);
    inject('schema-tattoo-artist-article', articleSchema);
    return () => {
      document.getElementById('schema-tattoo-artist-faq')?.remove();
      document.getElementById('schema-tattoo-artist-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πώς να Διαλέξεις Tattoo Artist στην Αθήνα | 210 Tattoo Athens</title>
        <meta name="description" content="Τι να προσέξεις πριν επιλέξεις tattoo studio στην Αθήνα: portfolio, στυλ, υγιεινή, consultation και red flags. Οδηγός από το 210 Tattoo Athens." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-artist-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-artist-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-artist-athens" />
        <meta property="og:title" content="Πώς να Διαλέξεις Tattoo Artist στην Αθήνα | 210 Tattoo Athens" />
        <meta property="og:description" content="Τι να προσέξεις πριν επιλέξεις tattoo studio στην Αθήνα: portfolio, στυλ, υγιεινή, consultation και red flags." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-artist-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-artist-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Πώς να Διαλέξεις Tattoo Artist στην Αθήνα | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Τι να προσέξεις πριν επιλέξεις tattoo studio στην Αθήνα. Οδηγός από το 210 Tattoo Athens." />
        <meta name="twitter:image" content={ogImage('tattoo-artist-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Tattoo Artist Αθήνα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Τι να προσέξεις</span>
              <br />
              <span className="text-white">πριν επιλέξεις tattoo studio στην Αθήνα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">24 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Η επιλογή tattoo artist επηρεάζει ένα μόνιμο αποτέλεσμα στο σώμα σου. Αυτός ο οδηγός σε βοηθά να κάνεις τη σωστή επιλογή.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Instagram aesthetics warning */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Το Instagram δεν είναι <span className="text-pink-500">αρκετό κριτήριο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Το πιο κοινό λάθος είναι να επιλέγεις artist βάσει feed aesthetics. Ένα καλαίσθητο Instagram δεν σημαίνει απαραίτητα καλό tattoo. Τα φωτογραφισμένα fresh tattoos μπορούν να παραπλανήσουν, το μελάνι λάμπει, τα χρώματα είναι έντονα, οι γραμμές φαίνονται καθαρές. Το πραγματικό τεστ είναι το healed αποτέλεσμα.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Ψάξε αν ο artist αναρτά healed δουλειές. Αυτές φανερώνουν την πραγματική ποιότητα: πόσο κρατάνε οι γραμμές, πώς «κάθεται» το μελάνι, αν υπάρχουν blowouts ή fading. Αν δεν βρεις healed δουλειές, ρώτα στο consultation.
            </p>
          </div>
        </section>

        {/* Portfolio & specialization */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Portfolio και <span className="text-pink-500">εξειδίκευση στυλ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Κάθε tattoo artist έχει το στυλ στο οποίο αριστεύει. Εξειδίκευση σημαίνει βάθος τεχνικής σε ένα συγκεκριμένο στυλ, realism, fine line, traditional και watercolor απαιτούν ολοκληρωτικά διαφορετική τεχνική και ο καλός artist το γνωρίζει.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Ταίριασμα στυλ', desc: 'Το portfolio πρέπει να περιέχει δουλειές στο ίδιο στυλ που θέλεις. Αν θέλεις fine line, κοίτα κυρίως fine line έργα, όχι γενικό mix.' },
                { title: 'Συνέπεια ποιότητας', desc: 'Ένα-δυο εντυπωσιακά pieces δεν αρκούν. Κοίτα αν η ποιότητα είναι σταθερή σε όλο το portfolio.' },
                { title: 'Complexity και λεπτομέρεια', desc: 'Μπορεί ο artist να χειριστεί αυτό που έχεις στο μυαλό; Αν θέλεις κάτι πολύπλοκο, δες αν έχει κάνει αντίστοιχα projects.' },
                { title: 'Custom δουλειά vs flash', desc: 'Αν θέλεις custom σχέδιο, σιγουρέψου ότι ο artist κάνει custom, δεν κάνουν όλοι το ίδιο επίπεδο εξατομίκευσης.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hygiene */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Υγιεινή: <span className="text-pink-500">μη διαπραγματεύσιμο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ένα τατουάζ διεισδύει στο δέρμα. Τα standards υγιεινής είναι βασική επαγγελματική απαίτηση, και ένα σοβαρό studio εξηγεί τις πρακτικές του χωρίς ενόχληση, γιατί τις τηρεί ήδη πλήρως.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Single-use βελόνες', desc: 'Κάθε συνεδρία ξεκινά με καινούριες βελόνες. Ποτέ επαναχρησιμοποιούμενες.' },
                { title: 'Αποστειρωμένος εξοπλισμός', desc: 'Autoclave για τον εξοπλισμό που επαναχρησιμοποιείται. Το studio πρέπει να μπορεί να το επιβεβαιώσει.' },
                { title: 'Καθαρό περιβάλλον εργασίας', desc: 'Το station πρέπει να προετοιμάζεται μπροστά σου, με γάντια και κατάλληλη κάλυψη επιφανειών.' },
                { title: 'Άδεια λειτουργίας', desc: 'Το studio πρέπει να λειτουργεί νόμιμα. Δεν είναι κάτι που πρέπει να κρύβεται.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Reviews: τι <span className="text-pink-500">αξίζει να διαβάσεις</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Τα Google reviews και τα comments στο Instagram λένε πολλά, αν ξέρεις τι να ψάχνεις.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Επικοινωνία και επαγγελματισμός', desc: 'Πώς χειρίστηκε ο artist την επικοινωνία πριν και μετά τη συνεδρία; Αυτό φανερώνει επαγγελματισμό.' },
                { title: 'Τελικό αποτέλεσμα', desc: 'Ήταν το τελικό τατουάζ αυτό που συζητήθηκε στο consultation; Ο artist τήρησε την αρχική ιδέα;' },
                { title: 'Χειρισμός αρνητικής εμπειρίας', desc: 'Αν υπάρχει αρνητικό review, κοίτα πώς απάντησε το studio. Ο τρόπος διαχείρισης λέει πολλά.' },
                { title: 'Συνέπεια', desc: 'Ένα-δυο θαυμαστικά reviews δεν αρκούν. Ψάξε pattern, πολλοί πελάτες με παρόμοια θετική εμπειρία.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Consultation */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί το consultation <span className="text-pink-500">είναι απαραίτητο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το consultation είναι η στιγμή που καταλαβαίνεις αν ο artist κατανοεί αυτό που θέλεις, και αν εσύ έχεις σαφή εικόνα για το τι είναι εφικτό. Χρήσιμος χρόνος και για τις δύο πλευρές, και ένα studio που το παραλείπει δεν είναι σοβαρό studio.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Παρουσίασε την ιδέα σου', desc: 'References, σκίτσα, λεκτική περιγραφή. Ό,τι έχεις. Ένας καλός artist θα κάνει ερωτήσεις για να καταλάβει πλήρως το concept.' },
                { step: '02', title: 'Άκου τις προτάσεις', desc: 'Ένας έμπειρος artist μπορεί να προτείνει αλλαγές στο placement ή στο μέγεθος για καλύτερο αποτέλεσμα. Αυτό είναι καλό σημάδι, όχι κακό.' },
                { step: '03', title: 'Έλεγξε αν υπάρχει συνεννόηση', desc: 'Στο τέλος του consultation, πρέπει να φεύγεις με σαφή εικόνα του τι θα γίνει. Αν υπάρχει αβεβαιότητα, ρώτα ξανά.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Red flags */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Red flags που <span className="text-pink-500">δεν πρέπει να αγνοείς</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Υπάρχουν σήματα που πρέπει να σε κάνουν να αναθεωρήσεις πριν κλείσεις ραντεβού.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Δεν υπάρχει consultation', desc: 'Αν ο artist αρνείται ή αποφεύγει consultation και θέλει απευθείας ραντεβού, αυτό είναι πρόβλημα.' },
                { title: 'Πίεση για άμεση απόφαση', desc: 'Ένα σοβαρό studio δεν σε πιέζει. Σου δίνει χρόνο να αποφασίσεις.' },
                { title: 'Αδιαφορία για την ιδέα σου', desc: 'Αν ο artist δεν δείχνει ενδιαφέρον για το project σου στο consultation, αυτό θα φανεί και στο αποτέλεσμα.' },
                { title: 'Απουσία healed δουλειών', desc: 'Αν δεν μπορείς να δεις πώς γερνάνε τα τατουάζ του artist, ζήτα παραδείγματα ή κάνε επιπλέον έρευνα.' },
                { title: 'Ερωτήσεις υγιεινής που προκαλούν ενόχληση', desc: 'Αν ο artist ή το studio φαίνεται να ενοχλείται από ερωτήσεις για αποστείρωση, φύγε.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why 210 */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί το <span className="text-pink-500">210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Στο studio μας στην Αναστασίου Γενναδίου 34 στο Γκύζη, κάθε project ξεκινά με consultation. Δεν κλείνουμε ραντεβού χωρίς να έχουμε καταλάβει τι θέλεις και πώς μπορούμε να το αποδώσουμε σωστά.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Custom design για κάθε πελάτη', desc: 'Δεν δουλεύουμε από catalog. Κάθε σχέδιο γίνεται βάσει της δικής σου ιδέας.' },
                { title: 'Εξειδίκευση σε fine line και realism', desc: 'Έμπειρη ομάδα με αποδεδειγμένο portfolio σε συγκεκριμένα styles.' },
                { title: 'Αυστηρά πρωτόκολλα υγιεινής', desc: 'Single-use υλικά, αποστειρωμένος εξοπλισμός, καθαρό περιβάλλον, χωρίς εξαιρέσεις.' },
                { title: 'Πλήρες consultation πριν κάθε συνεδρία', desc: 'Βεβαιωνόμαστε ότι είμαστε στην ίδια σελίδα πριν μπει βελόνα στο δέρμα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Έτοιμος να κάνεις <span className="text-pink-500">το επόμενο βήμα;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας DM στο Instagram του 210 Tattoo Athens στην Αθήνα για να κλείσεις ραντεβού. Η διαδικασία είναι απλή: μοιράζεσαι την ιδέα σου, εμείς αξιολογούμε και σε κατευθύνουμε.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-cost-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πόσο κοστίζει ένα τατουάζ στην Αθήνα;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι επηρεάζει την τιμή: μέγεθος, στυλ, εμπειρία artist και custom design.</p>
              </Link>
              <Link to="/blog/walk-in-vs-appointment-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Walk-in ή Ραντεβού;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πότε να πας walk-in και πότε να κλείσεις ραντεβού, και πώς λειτουργεί η διαδικασία.</p>
              </Link>
              <Link to="/blog/pos-epilegeis-tattoo-artist-gia-stil-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς Διαλέγεις Tattoo Artist για το Στυλ σου</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πώς αναγνωρίζεις πραγματική εξειδίκευση ανά στυλ στην Αθήνα.</p>
              </Link>
              <Link to="/blog/tatouaz-kentro-athinas" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Γιατί Τατουάζ στο Κέντρο της Αθήνας</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι κερδίζεις επιλέγοντας στούντιο στο κέντρο.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
